import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { CheckCircle, Users } from "lucide-react";

export const PartyDetailsTab = ({
  partyFileRef, handlePartyFileUpload, partyData, setPartyData,
  uploading, uploadPartyDetails, partyUploadResult, clientId
}) => (
  <Card className="border-purple-200 bg-purple-50/50">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-purple-800">
        <Users className="w-5 h-5" />
        Upload Party Details (GSTIN & PAN)
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="p-3 bg-purple-100 rounded-lg border border-purple-300">
        <p className="text-sm text-purple-800">
          <strong>Purpose:</strong> Upload party GSTIN and PAN details from Tally XML or Excel file.
          If PAN is missing, it will be automatically derived from GSTIN (characters 3-12).
        </p>
      </div>

      <div className="space-y-3">
        <Label className="text-purple-700 font-medium">Upload Party File (XML or Excel)</Label>
        <div className="flex gap-3">
          <input
            ref={partyFileRef}
            type="file"
            accept=".xml,.xlsx,.xls"
            onChange={handlePartyFileUpload}
            className="flex-1 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200 cursor-pointer"
            data-testid="party-file-input"
          />
        </div>
        <p className="text-xs text-slate-500">
          Supports: Tally Party Ledger XML export, or Excel with columns: Party Name, GSTIN, PAN
        </p>
      </div>

      {partyData && (
        <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
          <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Parsed Party Details from: {partyData.filename}
          </h4>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">{partyData.total}</div>
              <div className="text-xs text-purple-600">Total Parties</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">{partyData.withPan}</div>
              <div className="text-xs text-green-600">With PAN</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">{partyData.withGstin}</div>
              <div className="text-xs text-blue-600">With GSTIN</div>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <div className="text-2xl font-bold text-amber-700">{partyData.derivedPan}</div>
              <div className="text-xs text-amber-600">PAN Derived</div>
            </div>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-purple-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-purple-900">#</th>
                  <th className="px-3 py-2 text-left text-purple-900">Party Name</th>
                  <th className="px-3 py-2 text-left text-purple-900">PAN</th>
                  <th className="px-3 py-2 text-left text-purple-900">GSTIN</th>
                  <th className="px-3 py-2 text-left text-purple-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {partyData.parties.slice(0, 50).map((party, idx) => (
                  <tr key={`${party.party_name}-${party.pan}-${party.gstin}`} className="border-b border-purple-100 hover:bg-purple-50">
                    <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{party.party_name}</td>
                    <td className="px-3 py-2 font-mono">
                      {party.pan || '-'}
                      {party.pan_derived && <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1 rounded">derived</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{party.gstin || '-'}</td>
                    <td className="px-3 py-2">
                      {party.pan
                        ? <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Ready</span>
                        : <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">No PAN</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {partyData.parties.length > 50 && (
              <p className="text-center text-sm text-slate-500 py-2">
                Showing first 50 of {partyData.parties.length} parties...
              </p>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setPartyData(null); if (partyFileRef.current) partyFileRef.current.value = ''; }}>Clear</Button>
            <Button onClick={uploadPartyDetails} disabled={uploading || !clientId} className="bg-purple-600 hover:bg-purple-700" data-testid="upload-party-btn">
              {uploading ? 'Uploading...' : `Upload ${partyData.total} Parties`}
            </Button>
          </div>
        </div>
      )}

      {partyUploadResult && (
        <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Party Details Updated Successfully
          </h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-xl font-bold text-green-700">{partyUploadResult.created}</div>
              <div className="text-xs text-green-600">New Parties Created</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-700">{partyUploadResult.updated}</div>
              <div className="text-xs text-blue-600">Existing Updated</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-purple-700">{partyUploadResult.total}</div>
              <div className="text-xs text-purple-600">Total Processed</div>
            </div>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);
